/* ============================================================
   ProviderPayoutPanel — SD-4 (spec §5): the performer's Safe Deal
   payout destination. Card synonym (PayoutsData widget token) or
   YooMoney wallet number; masked read-back, replace = full swap.

   21f0c169: when the payout gateway agentId is configured
   (GET /providers/cabinet/payout-widget-config → enabled), the card
   branch embeds the official YooKassa PayoutsData card-input widget —
   the performer types the PAN into YooKassa's own form, we receive
   only the payout_token synonym (+ last4 for the display mask).
   Without an agentId the branch falls back to the legacy manual-token
   input (local dev / pre-activation).
   ============================================================ */

import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../api/client';
import type { PayoutsDataInstance } from '../types/payouts-data';
import type { ProviderPayoutMethod, ProviderPayoutWidgetConfig } from '../types/provider';

/** Official YooKassa widget library (global window.PayoutsData). */
const WIDGET_SCRIPT_URL = 'https://yookassa.ru/payouts-data/3.1.0/widget.js';
const WIDGET_CONTAINER_ID = 'payout-form';
/** Project accent (tokens.css --color-primary) — the widget takes HEX only. */
const WIDGET_ACCENT = '#FF9500';

/** Idempotent module-scope loader — one script tag no matter how often the panel mounts. */
let widgetScriptPromise: Promise<void> | null = null;

function loadPayoutsDataScript(): Promise<void> {
  if (window.PayoutsData) return Promise.resolve();
  if (widgetScriptPromise) return widgetScriptPromise;
  widgetScriptPromise = new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = WIDGET_SCRIPT_URL;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => {
      widgetScriptPromise = null; // retryable on the next mount
      reject(new Error('payouts-data widget script failed to load'));
    };
    document.head.appendChild(script);
  });
  return widgetScriptPromise;
}

function formatWidgetError(code: string): string {
  switch (code) {
    case 'invalid_card_number': return 'Неверный номер карты — проверьте и введите заново.';
    case 'tokenization_error': return 'ЮKassa не смогла токенизировать карту — попробуйте ещё раз.';
    default: return `Виджет YooKassa вернул ошибку (${code}). Попробуйте ещё раз.`;
  }
}

export function ProviderPayoutPanel() {
  const [method, setMethod] = useState<ProviderPayoutMethod | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [widgetConfig, setWidgetConfig] = useState<ProviderPayoutWidgetConfig | null>(null);
  const [widgetError, setWidgetError] = useState<string | null>(null);
  const widgetInstance = useRef<PayoutsDataInstance | null>(null);

  const [kind, setKind] = useState<'card' | 'wallet'>('card');
  const [token, setToken] = useState('');
  const [wallet, setWallet] = useState('');
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [m, w] = await Promise.all([
        api.getProviderPayoutMethod(),
        api.getProviderPayoutWidgetConfig(),
      ]);
      setMethod(m);
      setWidgetConfig(w);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ошибка загрузки');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(); }, []);

  /** Widget success leg: persist the just-issued card synonym (+ last4 mask). */
  const saveCardToken = useCallback(async (payoutToken: string, last4?: string) => {
    setSaving(true);
    setError(null);
    setSavedAt(null);
    setWidgetError(null);
    try {
      const m = await api.updateProviderPayoutMethod({
        kind: 'card',
        payoutToken,
        cardLast4: last4,
      });
      setMethod(m);
      setSavedAt(m.displayMask);
      // The form has served its purpose — drop callbacks so a repeat
      // visit cannot fire the previous success handler again.
      widgetInstance.current?.clearListeners();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ошибка сохранения');
    } finally {
      setSaving(false);
    }
  }, []);

  /** Mount the widget into #payout-form whenever the card branch is visible + configured. */
  useEffect(() => {
    if (loading || kind !== 'card' || !widgetConfig?.enabled) return;

    let cancelled = false;
    setWidgetError(null);

    void loadPayoutsDataScript()
      .then(() => {
        if (cancelled) return;
        const PayoutsData = window.PayoutsData;
        if (!PayoutsData) {
          setWidgetError('Виджет YooKassa недоступен — обновите страницу.');
          return;
        }
        const instance = new PayoutsData({
          type: 'payout',
          account_id: widgetConfig.accountId,
          customization: { colors: { control_primary: WIDGET_ACCENT } },
          success_callback: (data) => { void saveCardToken(data.payout_token, data.last4); },
          error_callback: (code) => setWidgetError(formatWidgetError(code)),
        });
        widgetInstance.current = instance;
        instance.render(WIDGET_CONTAINER_ID).catch(() => {
          if (!cancelled) setWidgetError('Не удалось отобразить форму ввода карты.');
        });
      })
      .catch(() => {
        if (!cancelled) setWidgetError('Не удалось загрузить виджет YooKassa — проверьте соединение.');
      });

    return () => {
      cancelled = true;
      // SPA contract: remove callbacks + the rendered form on unmount.
      widgetInstance.current?.clearListeners();
      widgetInstance.current = null;
      document.getElementById(WIDGET_CONTAINER_ID)?.replaceChildren();
    };
  }, [loading, kind, widgetConfig, saveCardToken]);

  const save = async () => {
    setSaving(true);
    setError(null);
    setSavedAt(null);
    try {
      const m = await api.updateProviderPayoutMethod(
        kind === 'card'
          ? { kind: 'card', payoutToken: token.trim() }
          : { kind: 'wallet', walletNumber: wallet.trim() },
      );
      setMethod(m);
      setToken('');
      setWallet('');
      setSavedAt(m.displayMask);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ошибка сохранения');
    } finally {
      setSaving(false);
    }
  };

  const widgetActive = !loading && kind === 'card' && (widgetConfig?.enabled ?? false);
  const manualCardInput = !loading && kind === 'card' && !widgetActive;
  const canSave = kind === 'card' ? token.trim().length >= 8 : /^\d{11,16}$/.test(wallet.trim());

  return (
    <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-lg)', maxWidth: 560 }}>
      <div>
        <h2 style={{ fontSize: 'var(--fs-h2)', fontWeight: 'var(--fw-bold)' }}>Выплаты</h2>
        <p style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)', marginTop: 'var(--space-xs)' }}>
          Куда отправлять деньги за выполненные заказы. Средства хранятся в безопасной сделке,
          пока способ выплат не настроен.
        </p>
      </div>

      {loading ? (
        <div className="spinner" style={{ margin: '0 auto' }} />
      ) : (
        <>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: 'var(--space-sm) var(--space-md)',
              borderRadius: 'var(--radius-md)',
              background: 'var(--color-background)',
            }}
          >
            <span style={{ fontSize: 'var(--fs-body)', fontWeight: 'var(--fw-medium)' }}>
              {method?.configured ? method.displayMask : 'Способ не указан'}
            </span>
            {method?.configured && (
              <span className="badge badge-success">Активен</span>
            )}
          </div>

          {/* Kind chips */}
          <div style={{ display: 'flex', gap: 'var(--space-sm)' }}>
            <KindChip active={kind === 'card'} onClick={() => setKind('card')} label="Банковская карта" />
            <KindChip active={kind === 'wallet'} onClick={() => setKind('wallet')} label="Кошелёк YooMoney" />
          </div>

          {widgetActive && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-xs)' }}>
              <span style={{ fontSize: 'var(--fs-caption)', fontWeight: 'var(--fw-semibold)', color: 'var(--color-text-secondary)' }}>
                Данные карты (защищённая форма ЮKassa)
              </span>
              {/* The PayoutsData widget renders its card form here. */}
              <div id={WIDGET_CONTAINER_ID} style={{ minHeight: 120 }} />
              <span style={{ fontSize: 'var(--fs-overline)', color: 'var(--color-text-tertiary)' }}>
                Номер карты вводится в форме ЮKassa и не попадает на наши серверы —
                мы храним только токен для выплат.
              </span>
              {saving && (
                <p style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)' }}>Сохранение карты…</p>
              )}
              {widgetError && (
                <p style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-danger)' }}>{widgetError}</p>
              )}
            </div>
          )}

          {manualCardInput && (
            <label style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-xs)' }}>
              <span style={{ fontSize: 'var(--fs-caption)', fontWeight: 'var(--fw-semibold)', color: 'var(--color-text-secondary)' }}>
                Токен карты (виджет YooKassa)
              </span>
              <input
                type="text"
                value={token}
                onChange={(e) => setToken(e.target.value)}
                placeholder="Токен виджета PayoutsData"
                autoComplete="off"
                style={inputStyle}
              />
              <span style={{ fontSize: 'var(--fs-overline)', color: 'var(--color-text-tertiary)' }}>
                Номер карты вставлять нельзя — только токен виджета (мы не храним номера карт).
              </span>
            </label>
          )}

          {kind === 'wallet' && (
            <label style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-xs)' }}>
              <span style={{ fontSize: 'var(--fs-caption)', fontWeight: 'var(--fw-semibold)', color: 'var(--color-text-secondary)' }}>
                Номер кошелька
              </span>
              <input
                type="text"
                inputMode="numeric"
                value={wallet}
                onChange={(e) => setWallet(e.target.value)}
                placeholder="41001234567890"
                autoComplete="off"
                style={inputStyle}
              />
              <span style={{ fontSize: 'var(--fs-overline)', color: 'var(--color-text-tertiary)' }}>
                Идентифицированный или проверенный кошелёк, 11–16 цифр.
              </span>
            </label>
          )}

          {error && (
            <p style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-danger)' }}>{error}</p>
          )}
          {savedAt && !error && (
            <p style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-success)' }}>
              Способ выплат сохранён: {savedAt}
            </p>
          )}

          {/* The widget's own «Дальше» button submits the card form; the
              manual save button exists only for the fallback/wallet legs. */}
          {(kind === 'wallet' || manualCardInput) && (
            <button
              type="button"
              className="btn btn-primary"
              disabled={saving || !canSave}
              onClick={() => void save()}
            >
              {saving ? 'Сохранение…' : method?.configured ? 'Заменить способ' : 'Сохранить способ'}
            </button>
          )}
        </>
      )}
    </div>
  );
}

function KindChip({ active, onClick, label }: { active: boolean; onClick: () => void; label: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        flex: 1,
        padding: '10px var(--space-md)',
        borderRadius: 'var(--radius-md)',
        fontSize: 'var(--fs-body)',
        fontWeight: active ? 'var(--fw-semibold)' : 'var(--fw-medium)',
        color: active ? 'var(--color-text-primary)' : 'var(--color-text-secondary)',
        background: active ? 'var(--color-surface)' : 'var(--color-background)',
        border: active ? '1px solid var(--color-primary)' : '1px solid var(--color-separator)',
        cursor: 'pointer',
        transition: 'all var(--transition-fast)',
      }}
    >
      {label}
    </button>
  );
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  height: 48,
  padding: '12px 16px',
  fontSize: 'var(--fs-body)',
  borderRadius: 'var(--radius-md)',
  border: '1px solid var(--color-separator)',
  background: 'var(--color-surface)',
  color: 'var(--color-text-primary)',
  outline: 'none',
};
